/**
 * Token refresh bridge between Sandbox (plugin) and StudentPortal (shell).
 *
 * When Sandbox is embedded as an MFE inside StudentPortal, both apps share
 * the same cy-sso tokens. If both independently try to refresh on 401, they
 * race and cause duplicate refresh calls. This bridge lets Sandbox delegate
 * token refresh to StudentPortal so only one refresh call is made.
 *
 * Lifecycle:
 *   - `setTokenRefreshHandler(fn)` — called by sandboxPlugin.js on mount.
 *   - `clearTokenRefreshHandler()` — called by sandboxPlugin.js on unmount.
 *   - `getTokenRefreshHandler()` — called by createApiClient.js 401 interceptor.
 *
 * The handler is a function that returns a Promise resolving to
 * `{ accessToken, refreshToken }` after StudentPortal completes the refresh.
 *
 * HOW TO REMOVE (when retiring the MFE):
 *   1. Delete this file.
 *   2. Remove the import and calls in sandboxPlugin.js.
 *   3. Remove the `getTokenRefreshHandler()` check in createApiClient.js
 *      (the else-branch with the direct Axios refresh call stays).
 */

let _onTokenRefreshNeeded = null;

/**
 * Registers the token refresh handler provided by StudentPortal.
 * @param {function|null} fn — async function returning { accessToken, refreshToken }
 */
export function setTokenRefreshHandler(fn) {
  _onTokenRefreshNeeded = typeof fn === "function" ? fn : null;
}

/**
 * Clears the registered handler (called on unmount).
 */
export function clearTokenRefreshHandler() {
  _onTokenRefreshNeeded = null;
}

/**
 * Returns the registered token refresh handler, or null when Sandbox
 * is running in standalone mode (not embedded inside StudentPortal).
 * @returns {function|null}
 */
export function getTokenRefreshHandler() {
  return _onTokenRefreshNeeded;
}
